import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import type { EBIcon, IconConfig } from '@repo/sdk/models/components';

import { eaiControlledDeviceIdsAtom } from '@/state/eai-controlled-device-ids.state';

import { getDeviceIconConfig } from './get-device-icon-config';
import { useDeviceAlarmIconConfig } from './use-device-alarm-icon-config';

export type UseResolvedDeviceIconConfigParams = {
  deviceId: string;
  icon: EBIcon;
  fallbackIcon?: string;
  /** When true, return undefined if no active icon state or alarm (matches {@link DeviceIconContent}). */
  hideWhenInactive?: boolean;
};

/**
 * Resolves the icon config that {@link DeviceIconContent} would render (alarm wins, then active state).
 * Use to align layout with dynamic icon visibility (e.g. hideWhenInactive / EAI hydration).
 */
export const useResolvedDeviceIconConfig = ({
  deviceId,
  icon,
  fallbackIcon = 'Settings',
  hideWhenInactive = false,
}: UseResolvedDeviceIconConfigParams): IconConfig | undefined => {
  const alarmIconConfig = useDeviceAlarmIconConfig(deviceId);
  const eaiControlledDeviceIds = useAtomValue(eaiControlledDeviceIdsAtom);

  return useMemo(() => {
    void eaiControlledDeviceIds;
    return alarmIconConfig ?? getDeviceIconConfig(icon, fallbackIcon, hideWhenInactive);
  }, [alarmIconConfig, eaiControlledDeviceIds, fallbackIcon, hideWhenInactive, icon]);
};
